import { useId, useMemo, useState } from "react";
import { Area, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatUSD } from "../lib/format";
import { fmtDateFr, fmtIntFr, fmtPctFr } from "../lib/portfolioMeta";

const PERIODS = [
  { id: "1m", label: "1M", days: 30 },
  { id: "3m", label: "3M", days: 91 },
  { id: "1y", label: "1A", days: 365 },
  { id: "all", label: "Tout", days: null },
];

// Garde les points des `days` derniers jours (par rapport au dernier point de la courbe)
function slicePeriod(curve, days) {
  if (!days || curve.length === 0) return curve;
  const lastDate = new Date(curve[curve.length - 1].date);
  const from = new Date(lastDate.getTime() - days * 864e5);
  const out = curve.filter((p) => new Date(p.date) >= from);
  return out.length >= 2 ? out : curve.slice(-2);
}

/**
 * Courbe de performance de la page détail.
 * - navCurve : [{date, index}] (indice base 10 000) — visible par tous, même portefeuille verrouillé
 * - valueCurve : [{date, value, invested}] — admin uniquement (bascule « Valeur $ »)
 */
export default function PortfolioChart({ navCurve = [], valueCurve = [], ath, showValueToggle = false }) {
  const gid = useId().replace(/:/g, "");
  const [period, setPeriod] = useState("all");
  const [mode, setMode] = useState("index"); // "index" | "value"
  const valueMode = showValueToggle && mode === "value" && valueCurve.length > 0;
  const days = PERIODS.find((p) => p.id === period)?.days;

  const data = useMemo(
    () => slicePeriod(valueMode ? valueCurve : navCurve, days),
    [valueMode, valueCurve, navCurve, days]
  );
  const key = valueMode ? "value" : "index";
  const first = data[0]?.[key];
  const last = data[data.length - 1]?.[key];
  // En mode indice, la perf de la période se lit directement sur l'indice
  const periodPerf = !valueMode && first ? (last / first - 1) * 100 : null;
  const up = valueMode ? true : (periodPerf ?? 0) >= 0;
  const color = up ? "#00BF63" : "#fb7185";

  if (navCurve.length < 2) {
    return (
      <div className="rounded-xl border border-white/5 bg-card p-6">
        <h2 className="font-display font-bold text-lg">Évolution</h2>
        <div className="py-16 text-center text-zinc-500">Historique en cours de constitution.</div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/5 bg-card p-6" data-testid="sideb-chart-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display font-bold text-lg">Évolution</h2>
          {periodPerf !== null && (
            <p className="text-xs text-zinc-500 mt-0.5">
              Sur la période :{" "}
              <span className={`font-mono-num ${up ? "text-emerald-400" : "text-rose-400"}`} data-testid="sideb-period-perf">
                {fmtPctFr(periodPerf)}
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {showValueToggle && valueCurve.length > 0 && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-white/10 p-1 text-[11px]">
              {[
                ["index", "Indice"],
                ["value", "Valeur $"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMode(id)}
                  className={`px-2 py-1 rounded ${mode === id ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-300"}`}
                  data-testid={`sideb-chart-mode-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="inline-flex items-center gap-1 rounded-lg bg-white/[0.03] border border-white/5 p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                  period === p.id ? "bg-emerald-500 text-black font-medium" : "text-zinc-400 hover:text-zinc-200"
                }`}
                data-testid={`sideb-period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="h-72" data-testid="sideb-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={color} stopOpacity={0.22} />
                <stop offset="1" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#71717a" }}
              tickFormatter={fmtDateFr}
              minTickGap={60}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#71717a" }}
              domain={["auto", "auto"]}
              tickFormatter={valueMode ? (v) => formatUSD(v) : fmtIntFr}
              width={valueMode ? 70 : 56}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
              labelStyle={{ color: "#a1a1aa", fontSize: 12 }}
              labelFormatter={fmtDateFr}
              formatter={(v, k) =>
                valueMode
                  ? [formatUSD(v), k === "invested" ? "Capital net investi" : "Valeur"]
                  : [`${fmtIntFr(v)} (${fmtPctFr((v / 10000 - 1) * 100)})`, "Indice"]
              }
            />
            {!valueMode && (
              <ReferenceLine
                y={10000}
                stroke="#a1a1aa"
                strokeDasharray="4 4"
                strokeOpacity={0.45}
                label={{ value: "Base 10 000", fill: "#71717a", fontSize: 10, position: "insideBottomRight" }}
              />
            )}
            {!valueMode && ath != null && (
              <ReferenceLine
                y={ath}
                stroke="#00BF63"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
                label={{ value: "Plus haut", fill: "#00BF63", fontSize: 10, position: "insideTopRight" }}
              />
            )}
            <Area
              type="monotone"
              dataKey={key}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gid})`}
              dot={false}
              isAnimationActive={false}
              name={valueMode ? "Valeur" : "Indice"}
            />
            {valueMode && (
              <Line
                type="monotone"
                dataKey="invested"
                stroke="#71717a"
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="3 3"
                name="Capital net investi"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
